import { PayloadAction, createSlice } from '@reduxjs/toolkit';

export interface IReason {
    id: string;
    title: string;
    value: string;
    isSomethingElse: boolean;
    subReason: string | undefined;
}

export const initialState: any = {
    sessionSource: null,
    selectedStylist: null,
    stylistToViewDetail: null,
    sourceStoreId: null,
    sessionPurpose: null,
    sessionLocation: null,
    sessionLocationAnswerId: null,
    sessionSeason: null,
    sessionSeasonAnswerId: null,
    sessionEnvironment: null,
    sessionEnvironmentAnswerId: null,
    sessionTheme: null,
    sessionThemeCustomValue: '',
    sessionThemeAnswerId: null,
    sessionStylePreferences: null,
    sessionPreferencesCustomValue: '',
    sessionStylePreferencesAnswerId: null,
    sessionFocusedItems: null,
    sessionFocusedItemsCustomValue: '',
    sessionFocusedItemsAnswerId: null,
    sessionExpectedItems: null,
    sessionExpectedCustomValue: '',
    sessionExpectedItemsAnswerId: null,
    sessionWardrobe: null,
    sessionWardrobeAnswerId: null,
    sessionMixAndMatch: null,
    sessionMixAndMatchAnswerId: null,
    quizId: null,
    questions: null,
    sessionId: null,
    completedQuizzes: [],
    requestQuizPath: null,
    sessionTaffiId: null,
    reschedulePath: null,
    requestQuizUpdatePage: false,
    isUserAttendingRequestQuiz: false,
    prevSessionQuiz: [],
};

const requestQuizSlice = createSlice({
    name: 'requestQuiz',
    initialState,
    reducers: {
        setSessionSource: (state, action) => {
            state.sessionSource = action.payload;
        },
        setSelectedStylist: (state, action) => {
            state.selectedStylist = action.payload;
        },
        setStylistToViewDetail: (state, action) => {
            state.stylistToViewDetail = action.payload;
        },
        setSourceStoreId: (state, action) => {
            state.sourceStoreId = action.payload;
        },
        setSessionPurpose: (state, action: PayloadAction<IReason>) => {
            state.sessionPurpose = action.payload;
        },
        setSessionLocation: (state, action) => {
            state.sessionLocation = action.payload;
        },
        setSessionSeason: (state, action) => {
            state.sessionSeason = action.payload;
        },
        setSessionEnvironment: (state, action) => {
            state.sessionEnvironment = action.payload;
        },
        setSessionTheme: (state, action) => {
            state.sessionTheme = action.payload;
        },
        setSessionStylePreferences: (state, action) => {
            state.sessionStylePreferences = action.payload;
        },
        setSessionFocusedItems: (state, action) => {
            state.sessionFocusedItems = action.payload;
        },
        setSessionExpectedItems: (state, action) => {
            state.sessionExpectedItems = action.payload;
        },
        setSessionWardrobe: (state, action) => {
            state.sessionWardrobe = action.payload;
        },
        setSessionMixAndMatch: (state, action) => {
            state.sessionMixAndMatch = action.payload;
        },
        setCurrentRequestQuizId: (state, action) => {
            state.quizId = action.payload;
        },
        setSessionQuestion: (state, action) => {
            state.questions = action.payload;
        },
        setSessionId: (state, action) => {
            state.sessionId = action.payload;
        },
        setCompletedQuizzes: (state, action) => {
            state.completedQuizzes = Array.from(new Set([...state.completedQuizzes, action.payload]));
        },
        removeCompletedQuiz: (state, action) => {
            const quizList = state.completedQuizzes.filter((quiz) => quiz !== action.payload);
            state.completedQuizzes = quizList;
        },
        setRequestQuizPath: (state, action) => {
            state.requestQuizPath = action.payload;
        },
        setSessionTaffiId: (state, action) => {
            state.sessionTaffiId = action.payload;
        },
        resetRequestQuiz: (state) => {
            return { ...initialState };
        },
        setSessionReschedulePath: (state, action) => {
            state.reschedulePath = action.payload;
        },
        setRequestQuizUpdatePage: (state, action) => {
            state.requestQuizUpdatePage = action.payload;
        },
        setIsUserAttendingRequestQuiz: (state, action) => {
            state.isUserAttendingRequestQuiz = action.payload;
        },
        setSessionLocationAnswerId: (state, action) => {
            state.sessionLocationAnswerId = action.payload;
        },
        setSessionSeasonAnswerId: (state, action) => {
            state.sessionSeasonAnswerId = action.payload;
        },
        setSessionEnvironmentAnswerId: (state, action) => {
            state.sessionEnvironmentAnswerId = action.payload;
        },
        setSessionThemeCustomValue: (state, action) => {
            state.sessionThemeCustomValue = action.payload;
        },
        setSessionPreferencesCustomValue: (state, action) => {
            state.sessionPreferencesCustomValue = action.payload;
        },
        setSessionFocusedItemsCustomValue: (state, action) => {
            state.sessionFocusedItemsCustomValue = action.payload;
        },
        setSessionExpectedCustomValue: (state, action) => {
            state.sessionExpectedCustomValue = action.payload;
        },
        setSessionThemeAnswerId: (state, action) => {
            state.sessionThemeAnswerId = action.payload;
        },
        setSessionStylePreferencesAnswerId: (state, action) => {
            state.sessionStylePreferencesAnswerId = action.payload;
        },
        setSessionFocusedItemsAnswerId: (state, action) => {
            state.sessionFocusedItemsAnswerId = action.payload;
        },
        setSessionExpectedItemsAnswerId: (state, action) => {
            state.sessionExpectedItemsAnswerId = action.payload;
        },
        setSessionWardrobeAnswerId: (state, action) => {
            state.sessionWardrobeAnswerId = action.payload;
        },
        setSessionMixAndMatchAnswerId: (state, action) => {
            state.sessionMixAndMatchAnswerId = action.payload;
        },
        setPrevSessionQuiz: (state, action) => {
            state.prevSessionQuiz = Array.from(new Set([...state.prevSessionQuiz, action.payload]));
        },
        removePrevSessionQuiz: (state, action) => {
            const quizList = state.prevSessionQuiz.filter((quiz) => quiz?.quiz !== action.payload?.quiz);
            state.prevSessionQuiz = quizList;
        },
    },
});

export const {
    setSessionSource,
    setSelectedStylist,
    setStylistToViewDetail,
    setSourceStoreId,
    resetRequestQuiz,
    setSessionPurpose,
    setSessionLocation,
    setSessionSeason,
    setSessionEnvironment,
    setSessionTheme,
    setSessionThemeCustomValue,
    setSessionStylePreferences,
    setSessionPreferencesCustomValue,
    setSessionFocusedItems,
    setSessionFocusedItemsCustomValue,
    setSessionExpectedItems,
    setSessionExpectedCustomValue,
    setSessionWardrobe,
    setSessionMixAndMatch,
    setCurrentRequestQuizId,
    setSessionQuestion,
    setSessionId,
    setCompletedQuizzes,
    setRequestQuizPath,
    setSessionTaffiId,
    removeCompletedQuiz,
    setSessionReschedulePath,
    setRequestQuizUpdatePage,
    setIsUserAttendingRequestQuiz,
    setSessionEnvironmentAnswerId,
    setSessionExpectedItemsAnswerId,
    setSessionFocusedItemsAnswerId,
    setSessionLocationAnswerId,
    setSessionMixAndMatchAnswerId,
    setSessionSeasonAnswerId,
    setSessionStylePreferencesAnswerId,
    setSessionThemeAnswerId,
    setSessionWardrobeAnswerId,
    setPrevSessionQuiz,
    removePrevSessionQuiz,
} = requestQuizSlice.actions;

const requestQuizReducer = requestQuizSlice.reducer;

export default requestQuizReducer;
